import React, { useMemo } from 'react';
import { ReactComponent as FileNewEmptyIcon } from '@repo/common/assets/icons/24px/icon_24px_file-new-empty_outline.svg';
import PriorityActions from './Priority/PriorityActions';
import RecommendActions from './Recommend/RecommendActions';
import VisaStatusCard from './Evidence/VisaStatusCard';
import LanguageProficiencyCard from './Evidence/LanguageProficiencyCard';
import AdditionalEvidenceCard from './Evidence/AdditionalEvidenceCard';
import { getRequestedEvidenceItemsWithFallback } from './EvidenceModal/EvidenceModal';
import {
  createPathfinderEvidenceFile,
  deletePathfinderEvidenceFile,
  updatePathfinderEvidenceStatus,
} from '../../../../../api/pathfinder/pathfinder.api';

const LANGUAGE_EVIDENCE_IDS = new Set(['topik', 'institute-certificate-kr']);
const VISA_EVIDENCE_IDS = new Set(['visa-copy']);

const toArray = (value) => (Array.isArray(value) ? value : []);

const groupEvidenceFilesByVariable = (files = []) =>
  toArray(files).reduce((acc, file) => {
    if (!file?.variableName) return acc;
    return {
      ...acc,
      [file.variableName]: [...(acc[file.variableName] || []), file],
    };
  }, {});

const toPersistedFileItem = (file) => ({
  id: `persisted-${file.id}`,
  persistedId: file.id,
  key: file.s3Key,
  status: 'success',
  progress: 100,
  loaded: file.fileSize || 0,
  createdAt: file.createdAt ? Date.parse(file.createdAt) : 0,
  fileName: file.fileName,
  file: {
    name: file.fileName,
    size: file.fileSize || 0,
    type: file.contentType || '',
  },
});

const hasValidLinks = (links = []) =>
  toArray(links).some((link) => link?.linkUrl?.trim());

const getPrimaryVariableName = (variableName) => {
  if (!variableName) return '';
  return String(variableName).split(/\s+or\s+|,/i)[0].trim();
};

const normalizePriorityActions = (evaluationData) => {
  const items = toArray(evaluationData?.recommendationData?.priorityActions);
  if (!evaluationData) return undefined;
  if (items.length === 0) return [];

  return items.map((item, index) => ({
    id: `${item.categoryCode || 'priority'}-${index}`,
    scoreLabel: item.scoreLabel,
    title: item.title || item.categoryCode,
    actionText: item.actionText,
    url: item.linkUrl,
  }));
};

const MAX_RECOMMEND_ACTIONS = 5;

const normalizeRecommendActions = (evaluationData) => {
  const items = toArray(evaluationData?.recommendationData?.recommendActions);
  if (!evaluationData) return undefined;
  if (items.length === 0) return [];

  const seen = new Set();
  return items
    .filter((item) => {
      const key = item.id ?? item.title;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, MAX_RECOMMEND_ACTIONS)
    .map((item, index) => ({
      id: item.id || `${item.skillCode || 'recommend'}-${index}`,
      title: item.title,
      moduleName: item.moduleName,
      description: item.description,
      buttonLabel: item.buttonLabel,
      url: item.linkUrl,
    }));
};

function EvidenceSection({ sessionId, evaluationData, evidenceCards, onEvidenceUpdated }) {
  const requestedEvidenceItems = useMemo(
    () => getRequestedEvidenceItemsWithFallback(evaluationData),
    [evaluationData]
  );
  const filesByVariable = groupEvidenceFilesByVariable(evidenceCards?.files);
  const evidenceModalItems = requestedEvidenceItems.map((item) => {
    const variableName = getPrimaryVariableName(item.variableName);
    const persistedFiles = filesByVariable[variableName] || [];
    const uploadedFromServer = persistedFiles.length > 0;

    return {
      ...item,
      variableName,
      state: uploadedFromServer ? 'uploaded' : item.state,
      fileName: uploadedFromServer
        ? persistedFiles[0]?.fileName || item.fileName || 'Uploaded evidence'
        : item.fileName,
      files: persistedFiles.map(toPersistedFileItem),
    };
  });
  const visaItems = evidenceModalItems.filter((item) => VISA_EVIDENCE_IDS.has(item.id));
  const languageItems = evidenceModalItems.filter((item) => LANGUAGE_EVIDENCE_IDS.has(item.id));
  const additionalItems = evidenceModalItems.filter(
    (item) => !VISA_EVIDENCE_IDS.has(item.id) && !LANGUAGE_EVIDENCE_IDS.has(item.id)
  );
  const hasUploadedVisaFile = visaItems.some((item) => toArray(item.files).length > 0);
  const uploadedLanguageItems = languageItems.filter((item) => toArray(item.files).length > 0);
  const uploadedAdditionalItems = additionalItems.filter((item) => toArray(item.files).length > 0);
  const visaChips = hasUploadedVisaFile && evidenceCards?.visaStatus?.chipLabel
    ? [{ label: evidenceCards.visaStatus.chipLabel }]
    : [];
  const languageLevels =
    uploadedLanguageItems.length > 0 && evidenceCards?.languageStatus?.chips?.length > 0
      ? evidenceCards.languageStatus.chips
      : uploadedLanguageItems.map((item) => item.title);
  const additionalEvidenceLabels =
    uploadedAdditionalItems.length > 0 && evidenceCards?.additionalStatus?.chips?.length > 0
      ? evidenceCards.additionalStatus.chips
      : uploadedAdditionalItems.map((item) => item.title);
  const initialScore = Number.isFinite(Number(evaluationData?.finalScore))
    ? Number(evaluationData.finalScore)
    : 0;

  const handleEvidenceSave = async (item) => {
    if (!sessionId || !item?.variableName) return;

    const currentFiles = toArray(item.files);
    const persistedIds = new Set(
      currentFiles.map((file) => file.persistedId).filter(Boolean)
    );
    const previousPersistedIds = new Set(
      toArray(evidenceCards?.files)
        .filter((file) => file.variableName === item.variableName)
        .map((file) => file.id)
    );
    const deletedFileIds = [...previousPersistedIds].filter((id) => !persistedIds.has(id));
    const newFiles = currentFiles.filter(
      (file) => file.status === 'success' && file.key && !file.persistedId
    );
    const itemHasLinks = hasValidLinks(item.links);
    let result = null;

    for (const fileId of deletedFileIds) {
      result = await deletePathfinderEvidenceFile(sessionId, fileId);
    }

    for (const fileItem of newFiles) {
      result = await createPathfinderEvidenceFile(sessionId, {
        variableName: item.variableName,
        fileName: fileItem.file?.name || fileItem.fileName,
        s3Key: fileItem.key,
        contentType: fileItem.file?.type || '',
        fileSize: fileItem.file?.size || 0,
      });
    }

    if (item.state === 'uploaded' && newFiles.length === 0 && (deletedFileIds.length === 0 || itemHasLinks)) {
      result = await updatePathfinderEvidenceStatus(sessionId, item.variableName, 'UPLOADED');
    }

    await onEvidenceUpdated?.(result);
  };

  return (
    <section className='flex flex-col items-start w-full gap-5 shrink-0'>
      <div className='flex w-full items-start gap-1.5 px-5'>
        <span className='flex h-[30px] shrink-0 items-center text-icon-primary'>
          <FileNewEmptyIcon className='size-6' />
        </span>
        <h2 className='typo-b20Strong text-text-primary'>Evidence</h2>
      </div>

      {/* 모바일: 가로 슬라이드(size-[300px] 고정) / 데스크탑: flex-wrap */}
      <div className='flex w-[calc(100%+16px)] gap-[24px] overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden tabletV:w-full tabletV:flex-wrap tabletV:gap-[30px] tabletV:overflow-visible tabletV:pb-0 min-[1760px]:flex-nowrap'>
        {[ 
          <VisaStatusCard
            visa={visaItems.length > 0 ? { type: 'Visa evidence required', expired: false } : null}
            chips={visaChips}
            evidenceItems={visaItems}
            sessionId={sessionId}
            initialScore={initialScore}
            onEvidenceSave={handleEvidenceSave}
          />,
          <LanguageProficiencyCard
            levels={languageLevels}
            evidenceItems={languageItems}
            sessionId={sessionId}
            initialScore={initialScore}
            onEvidenceSave={handleEvidenceSave}
          />,
          <AdditionalEvidenceCard
            evidences={additionalEvidenceLabels}
            evidenceItems={additionalItems}
            sessionId={sessionId}
            initialScore={initialScore}
            onEvidenceSave={handleEvidenceSave}
          />,
        ].map((card, i) => (
          <div
            key={i}
            className='size-[300px] shrink-0 tabletV:size-auto tabletV:aspect-square tabletV:flex-[1_0_0] tabletV:min-w-[280px] tabletV:max-w-[350px] tabletV:min-h-[280px] tabletV:max-h-[350px] tabletV:shrink desktopL:basis-[336.667px] desktopL:max-w-[336.667px] desktopL:min-h-0 desktopL:max-h-[336.667px]'
          >
            {card}
          </div>
        ))}
      </div>
    </section>
  );
}

function ActiveDefaultPage({
  sessionId,
  evaluationData,
  evidenceCards,
  onEvidenceUpdated,
}) {
  const priorityItems = normalizePriorityActions(evaluationData);
  const recommendItems = normalizeRecommendActions(evaluationData);

  return (
    <div className='flex h-full w-full flex-col items-start gap-10 tabletV:gap-[60px] desktopL:flex-row desktopL:gap-10'>
      <div className='flex w-full min-w-0 flex-1 flex-col items-start gap-10 tabletV:gap-[60px] tabletV:min-w-[730px] tabletV:max-w-[1110px] desktopL:h-full desktopL:min-w-0 desktopL:max-w-[1070px] desktopL:flex-[1_1_1070px] desktopL:gap-20'>
        <PriorityActions items={priorityItems} />
        <EvidenceSection
          sessionId={sessionId}
          evaluationData={evaluationData}
          evidenceCards={evidenceCards}
          onEvidenceUpdated={onEvidenceUpdated}
        />
      </div>

      <aside className='flex w-full tabletV:min-w-[360px] tabletV:max-w-[1110px] desktopL:h-full desktopL:min-w-0 desktopL:max-w-[574px] desktopL:flex-[1_1_574px]'>
        <RecommendActions items={recommendItems} />
      </aside>
    </div>
  );
}

export default ActiveDefaultPage;
